import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, CheckCircle2, FileText, Images, ListChecks, Lock, PlayCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { examLockState } from '../lib/exam-lock';
import { useCatalogTrack, useMyProgress } from '../lib/data';
import type { Step } from '../types';
import SiteHeader from './SiteHeader';

const TYPE_ICON = {
  video: PlayCircle,
  external: FileText,
  quiz: ListChecks,
  reading: BookOpen,
  slides: Images,
} as const;

export default function TrackView() {
  const { trackId } = useParams<{ trackId: string }>();
  const { user } = useAuth();
  // All audiences go through the catalog API — Firestore rules now only allow
  // direct track/step reads for admins, so we always use the access-checked API.
  const { track, steps, loading } = useCatalogTrack(trackId);
  const { progress, ready: progressReady } = useMyProgress(user?.uid, trackId);

  // Count only the steps the track has now: a progress doc keeps entries for steps that were
  // later removed from the track, and the stored completedSteps includes them.
  const done = steps.length
    ? steps.filter((s) => progress?.steps?.[s.id]?.done === true).length
    : (progress?.completedSteps ?? 0);
  const total = track?.stepCount || steps.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-paper-50 dark:bg-clay-floor">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 pb-16 pt-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-ink-400 hover:text-terracotta-600"
        >
          <ArrowLeft className="h-4 w-4" /> Kushtis
        </Link>

        {loading ? (
          <div className="py-12 text-center text-sm text-ink-400">Loading kushti…</div>
        ) : !track ? (
          <div className="py-12 text-center text-sm text-ink-400">Kushti not found.</div>
        ) : (
          <>
            <div className="mt-3 animate-rise-in">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold text-ink-900 dark:text-paper-50">{track.title}</h1>
                {track.internal && (
                  <span className="flex shrink-0 items-center gap-1 rounded-full bg-ink-100 px-2 py-0.5 text-[10px] font-semibold text-ink-500 dark:bg-clay-raised dark:text-paper-300">
                    <Lock className="h-3 w-3" />
                    Internal
                  </span>
                )}
              </div>
              {track.description && (
                <p className="mt-1 text-sm text-ink-500 dark:text-paper-300">{track.description}</p>
              )}
              {track.tags && track.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {track.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-paper-100 px-2 py-0.5 text-[10px] font-medium text-ink-400 dark:bg-clay-raised dark:text-paper-300"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 flex items-center gap-3">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-paper-200 dark:bg-clay-raised">
                  <div
                    className="h-full rounded-full bg-terracotta-500 transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-ink-400">
                  {done}/{total} paintras
                </span>
              </div>
            </div>

            <ol className="mt-6 flex flex-col gap-2">
              {steps.map((step, i) => (
                <StepRow
                  key={step.id}
                  trackId={track.id}
                  step={step}
                  index={i + 1}
                  done={progress?.steps?.[step.id]?.done === true}
                  locked={progressReady && examLockState(steps, progress, step.id).locked}
                />
              ))}
            </ol>
          </>
        )}
      </main>
    </div>
  );
}

function StepRow({
  trackId,
  step,
  index,
  done,
  locked,
}: {
  trackId: string;
  step: Step;
  index: number;
  done: boolean;
  locked: boolean;
}) {
  const Icon = TYPE_ICON[step.type] ?? FileText;
  return (
    <Link
      to={`/track/${trackId}/step/${step.id}`}
      className="group flex items-center gap-3 rounded-xl border border-rule bg-paper-0 p-3 transition hover:border-terracotta-200 dark:border-clay-rule dark:bg-clay-surface dark:hover:border-terracotta-700"
    >
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
          done
            ? 'bg-verdant-500 text-white'
            : 'bg-paper-100 text-ink-400 dark:bg-clay-raised dark:text-paper-300'
        }`}
      >
        {done ? <CheckCircle2 className="h-4 w-4" /> : index}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-ink-900 dark:text-paper-50">{step.title}</p>
        <p className="flex items-center gap-1.5 text-xs text-ink-400">
          <Icon className="h-3.5 w-3.5" />
          <span className="capitalize">{step.type}</span>
          {locked && (
            <>
              <span aria-hidden>·</span>
              <span className="inline-flex items-center gap-0.5 font-medium text-ink-500 dark:text-paper-300">
                <Lock className="h-3 w-3" aria-hidden />
                Locked
              </span>
            </>
          )}
          <span aria-hidden>·</span>
          <span className="font-medium text-terracotta-600 dark:text-terracotta-300">
            +{step.points} Zor
          </span>
        </p>
      </div>
    </Link>
  );
}
